import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { GroupFeed } from "@/components/GroupFeed";
import apiClient, { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Users, Plus, LogIn, Loader2, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function Groups() {
  const [groups, setGroups] = useState(null);
  const [open, setOpen] = useState(null);
  const [name, setName] = useState("");
  const [village, setVillage] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(null);

  const load = () => apiClient.get("/groups").then((r) => setGroups(r.data)).catch(() => setGroups([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (name.trim().length < 2) return toast.error("Give your group a name");
    setBusy("create");
    try {
      const r = await apiClient.post("/groups", { name, village });
      setGroups((g) => [r.data, ...(g || [])]); setName(""); setVillage(""); setOpen(r.data);
      toast.success(`Group created — share code ${r.data.code} with your neighbours`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setBusy(null); }
  };

  const join = async () => {
    if (code.trim().length < 4) return toast.error("Enter the 6-character group code");
    setBusy("join");
    try {
      const r = await apiClient.post("/groups/join", { code });
      setGroups((g) => [r.data, ...(g || []).filter((x) => x.id !== r.data.id)]); setCode(""); setOpen(r.data);
      toast.success(`Welcome to ${r.data.name}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-emerald-950 tracking-tight flex items-center gap-3"><Users className="h-8 w-8 text-[#2D6A4F]" /> Cooperative groups</h1>
        <p className="text-stone-600 mt-1 mb-8">Farmers in one village share sightings, treatment sheets and planting calendars privately.</p>

        {open ? (
          <GroupFeed group={open} onBack={() => { setOpen(null); load(); }} onLeft={() => { setOpen(null); load(); }} />
        ) : (
          <div className="grid lg:grid-cols-[1fr_380px] gap-6">
            <div>
              <h2 className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-3">My groups</h2>
              {groups === null ? <div className="text-sm text-stone-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
                : groups.length === 0 ? <div data-testid="groups-empty" className="bg-white rounded-2xl border border-dashed border-stone-300 p-10 text-center text-stone-600">You're not in a group yet. Create one for your village or join with a code.</div>
                : (
                  <ul className="space-y-3" data-testid="groups-list">
                    {groups.map((g) => (
                      <li key={g.id}>
                        <button data-testid={`group-${g.id}`} onClick={() => setOpen(g)} className="w-full text-left bg-white rounded-2xl border border-stone-200 p-5 flex items-center gap-4 hover:border-[#52B788] hover:shadow-lg transition-all">
                          <div className="h-12 w-12 rounded-2xl bg-[#EAF6EF] text-[#2D6A4F] flex items-center justify-center font-display font-bold text-lg">{g.name.slice(0, 1)}</div>
                          <div className="flex-1 min-w-0">
                            <div className="font-display font-bold text-emerald-950 truncate">{g.name}</div>
                            <div className="text-xs text-stone-500">{g.village ? `${g.village} · ` : ""}{g.members} member{g.members !== 1 ? "s" : ""} · code <span className="font-mono-tech font-bold text-[#78350F]">{g.code}</span></div>
                          </div>
                          <ChevronRight className="h-5 w-5 text-stone-400" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
            </div>
            <aside className="space-y-4">
              <div className="bg-white rounded-2xl border border-stone-200 p-5">
                <h3 className="font-display font-bold text-emerald-950 mb-3 flex items-center gap-2"><LogIn className="h-4 w-4 text-[#D97706]" /> Join with a code</h3>
                <div className="flex gap-2">
                  <Input data-testid="join-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. 1F0C39" maxLength={12} className="bg-white font-mono-tech uppercase" />
                  <Button data-testid="join-group-btn" onClick={join} disabled={busy === "join"} className="bg-[#D97706] hover:bg-[#78350F] text-white">{busy === "join" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Join"}</Button>
                </div>
              </div>
              <div className="bg-[#143D29] text-emerald-50 rounded-2xl p-5">
                <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Plus className="h-4 w-4 text-[#52B788]" /> Start a group</h3>
                <Label className="text-[11px] uppercase tracking-wider text-emerald-200/70 font-semibold">Group name</Label>
                <Input data-testid="group-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Nakuru Maize Growers" className="mt-1 mb-3 bg-white/10 border-white/20 text-white placeholder:text-emerald-100/40" />
                <Label className="text-[11px] uppercase tracking-wider text-emerald-200/70 font-semibold">Village (optional)</Label>
                <Input data-testid="group-village-input" value={village} onChange={(e) => setVillage(e.target.value)} placeholder="e.g. Nakuru" className="mt-1 mb-4 bg-white/10 border-white/20 text-white placeholder:text-emerald-100/40" />
                <Button data-testid="create-group-btn" onClick={create} disabled={busy === "create"} className="w-full bg-[#52B788] hover:bg-[#3f9a70] text-[#143D29] font-semibold">{busy === "create" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create group"}</Button>
              </div>
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}
